import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { CheckCircle, Clock, FileText, Hand, BadgeCheck, Mail, Pencil, Check, X, Loader2, UserCog } from 'lucide-react';
import { useState, useEffect } from 'react';
import { LazyInvoiceImage } from './LazyInvoiceImage';
import { InvoiceImageModal } from './InvoiceImageModal';
import { useAuth } from '../../context/AuthContext';

const BANKS = { P: 'PNC', T: 'TD Bank', C: 'Capital One', R: 'Regions Bank', W: 'Wise', H: 'Cash App', L: 'PayPal', S: 'Stripe USA', SS: 'Stripe Internacional', B: 'Bank of America', A: 'American Express', E: 'Chase' };

const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

const Field = ({ label, children, testId, className = '' }) => (
    <div className={className} data-testid={testId}>
        <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
        <div className="text-slate-800 text-sm font-medium break-words">{children ?? '—'}</div>
    </div>
);

// Inline editor: pencil → input/select/textarea + save/cancel. onSave returns a promise.
const InlineEdit = ({ value, onSave, type = 'text', options, editTestId, canEdit, children, inputClass = '' }) => {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(value ?? '');
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (!editing) setVal(value ?? ''); }, [value, editing]);
    if (!canEdit) return children;
    const save = async () => {
        setBusy(true);
        try { await onSave(val); setEditing(false); } finally { setBusy(false); }
    };
    if (!editing) {
        return (
            <div className="flex items-start gap-1 group">
                <div className="min-w-0 flex-1">{children}</div>
                <button type="button" onClick={() => setEditing(true)} className="p-0.5 text-slate-400 hover:text-sky-600 shrink-0" title="Editar" data-testid={editTestId}>
                    <Pencil className="w-3.5 h-3.5" />
                </button>
            </div>
        );
    }
    const common = { value: val, autoFocus: true, disabled: busy, className: `border border-sky-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500 ${inputClass}`, 'data-testid': `${editTestId}-input` };
    const onKey = (e) => { if (e.key === 'Enter' && type !== 'textarea') save(); if (e.key === 'Escape') setEditing(false); };
    return (
        <div className="flex flex-wrap items-start gap-1 w-full">
            {type === 'select' ? (
                <select {...common} onChange={(e) => setVal(e.target.value)} onKeyDown={onKey}>
                    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            ) : type === 'textarea' ? (
                <textarea {...common} rows={4} onChange={(e) => setVal(e.target.value)} onKeyDown={onKey} />
            ) : (
                <input {...common} type={type} step="0.01" min="0.01" onChange={(e) => setVal(e.target.value)} onKeyDown={onKey} />
            )}
            <button type="button" onClick={save} disabled={busy} className="p-1 text-green-600 hover:bg-green-50 rounded shrink-0" data-testid={`${editTestId}-save`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}</button>
            <button type="button" onClick={() => setEditing(false)} disabled={busy} className="p-1 text-red-500 hover:bg-red-50 rounded shrink-0" data-testid={`${editTestId}-cancel`}><X className="w-4 h-4" /></button>
        </div>
    );
};

const CATEGORY_OPTIONS = Object.entries(BANKS).map(([value, label]) => ({ value, label: `${value} · ${label}` }));

export const InvoiceDetailModal = ({ invoice, onClose, formatCurrency, isAdmin = false, onAmountUpdate, onCategoryUpdate, onDescriptionUpdate, onReassign }) => {
    const { getAuthHeaders } = useAuth();
    const [imageView, setImageView] = useState(null);
    const vd = invoice?.verification_details || {};
    const canEdit = isAdmin && !!invoice;
    const saveAmount = async (v) => { const n = parseFloat(v); if (!(n > 0)) throw new Error('Monto inválido'); await onAmountUpdate?.(invoice.id, n); };
    return (
        <Dialog open={!!invoice} onOpenChange={() => onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-detail-modal">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-slate-800">Detalle de la orden {canEdit && <span className="ml-2 text-xs font-normal text-slate-400">(toca el lápiz para editar)</span>}</DialogTitle>
                </DialogHeader>
                {invoice && (
                    <div className="space-y-5">
                        <div className="flex items-start gap-4">
                            <div className="shrink-0" title="Toca para ver la imagen completa" data-testid="detail-thumb">
                                <LazyInvoiceImage invoiceId={invoice.id} invoiceName={invoice.name} getAuthHeaders={getAuthHeaders} onClick={setImageView} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-lg font-semibold text-slate-800 break-words" data-testid="detail-name">{invoice.name}</p>
                                <InlineEdit value={invoice.amount} type="number" onSave={saveAmount} canEdit={canEdit} editTestId="detail-edit-amount" inputClass="w-32">
                                    <p className="text-2xl font-bold text-sky-600" data-testid="detail-amount">
                                        {formatCurrency(invoice.amount)}
                                        {invoice.amount_edited && <span className="ml-2 text-xs font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">editado</span>}
                                    </p>
                                </InlineEdit>
                                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                    <span className={`badge ${invoice.confirmed ? 'badge-success' : 'badge-pending'}`} data-testid="detail-status">
                                        {invoice.confirmed ? <><CheckCircle className="w-3 h-3 mr-1" /> {invoice.auto_verified ? 'Verificado' : 'Confirmado'}</> : <><Clock className="w-3 h-3 mr-1" /> Pendiente</>}
                                    </span>
                                    {invoice.auto_verified && <span className="text-xs text-green-600 inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> automático</span>}
                                    {invoice.manual_only && !invoice.confirmed && <span className="text-[10px] font-semibold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full inline-flex items-center"><Hand className="w-3 h-3 mr-0.5" /> Solo manual</span>}
                                    {invoice.is_extra && <span className="text-[10px] font-semibold text-fuchsia-700 bg-fuchsia-100 px-1.5 py-0.5 rounded-full">Extra</span>}
                                </div>
                            </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="detail-description">
                            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1 inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Descripción</p>
                            <InlineEdit value={invoice.description || ''} type="textarea" onSave={(v) => onDescriptionUpdate?.(invoice.id, v)} canEdit={canEdit} editTestId="detail-edit-description" inputClass="w-full">
                                <p className="text-slate-700 text-sm whitespace-pre-wrap break-words">{invoice.description?.trim() ? invoice.description : <span className="text-slate-400 italic">Sin descripción</span>}</p>
                            </InlineEdit>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            <Field label="Usuario" testId="detail-user">
                                {canEdit && onReassign ? (
                                    <button type="button" onClick={() => onReassign(invoice)} className="inline-flex items-center gap-1 text-left hover:text-fuchsia-700" title="Cambiar usuario de la orden" data-testid="detail-reassign-btn">
                                        {invoice.user_name}
                                        <UserCog className="w-3.5 h-3.5 text-slate-400" />
                                    </button>
                                ) : invoice.user_name}
                            </Field>
                            <Field label="Categoría" testId="detail-category">
                                <InlineEdit value={invoice.category || 'P'} type="select" options={CATEGORY_OPTIONS} onSave={(v) => onCategoryUpdate?.(invoice.id, v)} canEdit={canEdit} editTestId="detail-edit-category" inputClass="w-full max-w-[150px]">
                                    <span>{invoice.category ? `${invoice.category} · ${BANKS[invoice.category] || '—'}` : '—'}</span>
                                </InlineEdit>
                            </Field>
                            <Field label="Subida" testId="detail-created">{fmtDate(invoice.created_at)}</Field>
                            <Field label="Confirmada" testId="detail-confirmed-at">{invoice.confirmed ? fmtDate(invoice.admin_confirmed_at || invoice.confirmed_at) : '—'}</Field>
                            {invoice.deposit_at && <Field label="Depósito" testId="detail-deposit-at">{fmtDate(invoice.deposit_at)}</Field>}
                            {invoice.original_amount != null && invoice.original_amount !== invoice.amount && <Field label="Monto original" testId="detail-original-amount">{formatCurrency(invoice.original_amount)}</Field>}
                        </div>

                        {(vd.bank || vd.sender_name) && (
                            <div className="rounded-xl border border-green-200 bg-green-50 p-4" data-testid="detail-verification">
                                <p className="text-xs uppercase tracking-wide text-green-700 mb-2 inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Verificación</p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    <Field label="Banco">{vd.bank}</Field>
                                    <Field label="Remitente">{vd.sender_name}</Field>
                                    <Field label="Monto email">{vd.amount != null ? formatCurrency(vd.amount) : '—'}</Field>
                                    <Field label="Email recibido" className="col-span-2 sm:col-span-3">{vd.date ? fmtDate(vd.date) : '—'}</Field>
                                </div>
                            </div>
                        )}

                        {(invoice.manual_only_reason || invoice.duplicate_reason) && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="detail-notes">
                                {invoice.manual_only_reason && <p><b>Solo manual:</b> {invoice.manual_only_reason}</p>}
                                {invoice.duplicate_reason && <p><b>Duplicado:</b> {invoice.duplicate_reason}</p>}
                            </div>
                        )}

                        {Array.isArray(invoice.amount_edit_history) && invoice.amount_edit_history.length > 0 && (
                            <div data-testid="detail-edit-history">
                                <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Historial de monto</p>
                                <ul className="text-sm text-slate-600 space-y-0.5">
                                    {invoice.amount_edit_history.map((h, i) => (
                                        <li key={i}>{formatCurrency(h.old_amount ?? h.from)} → <b>{formatCurrency(h.new_amount ?? h.to)}</b>{h.edited_by ? ` · ${h.edited_by}` : ''}{h.edited_at ? ` · ${fmtDate(h.edited_at)}` : ''}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
            <InvoiceImageModal invoice={imageView} isOpen={!!imageView} onClose={() => setImageView(null)} />
        </Dialog>
    );
};

export default InvoiceDetailModal;
